import {
  BillingPaymentMethod,
  BillingTransactionStatus,
  BillingTransactionType,
} from "@/types/billing-transactions";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { BillingTransaction } from "@/types/billing-transactions";
import { useBillingTransactions } from "@/hooks/useBillingTransactions";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import {
  useBookingPaymentSummary,
  useTopUpGuestWallet,
} from "@/hooks/useBookingPayments";
import { Role } from "@/types/user";
import {
  GuestWalletEntry,
  GuestWalletEntryType,
} from "@/types/booking-payments";
import {
  ArrowDownLeft,
  ArrowUpRight,
  Banknote,
  CreditCard,
  Loader2,
  User2,
  Wallet,
} from "lucide-react";
import { toast } from "sonner";
import { BalanceSummary } from "./BalanceSummary";
import { useToggle } from "@/hooks/useToggle";
import { cn } from "@/lib/utils";
import { getDistance } from "@/utils/dateUtils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { formatMoney } from "../../utils/formatMoney";
import { getPaymentUi } from "../../utils/getPaymentUi";
import { getStringMeta } from "../../utils/getStringMeta";
import { formatDateTime } from "../../utils/formatDateTime";
import { ToggleButton } from "@/components/pages/reservations/components/reservationDetails/ToggleButton";

const STAFF_WALLET_ROLES = new Set<Role>([
  Role.FRONT_DESK,
  Role.HOTEL_ADMIN,
  Role.TENANT_ADMIN,
]);

export const PaymentsInfo = ({
  bookingId,
  guestId,
  limit = 100,
}: {
  guestId?: string;
  bookingId?: string;
  limit?: number;
}) => {
  const { t } = useTranslation();
  const { userProfile } = useAuthenticatedUser();
  const role = userProfile?.role as Role | undefined;
  const canManageWallet = role ? STAFF_WALLET_ROLES.has(role) : false;
  const { value: isTransactionsOpened, handleToggle } = useToggle(true);
  const { value: isLogsOpened, handleToggle: handleToggleLogs } =
    useToggle(true);

  const filters = useMemo(
    () => ({
      bookingId,
      guestId,
      page: 1,
      limit,
    }),
    [bookingId, guestId, limit],
  );

  const query = useBillingTransactions(1, limit, filters);
  const summaryQuery = useBookingPaymentSummary(bookingId);

  const summary = summaryQuery.data;
  const data = query.data;
  const transactions = data?.transactions ?? [];

  if (query.isLoading || (bookingId && summaryQuery.isLoading && !summary)) {
    return null;
  }

  if (query.isError && !summary) {
    return null;
  }

  return (
    <>
      {summary ? (
        <WalletSummarySection
          summary={summary}
          bookingId={bookingId}
          canManageWallet={canManageWallet}
        />
      ) : null}

      {!summary && transactions.length > 0 ? (
        <BalanceSummary transactions={transactions} />
      ) : null}

      <div className="flex w-full flex-col gap-4">
        <ToggleButton
          title={t("billing.transactionHistory")}
          isOpened={isTransactionsOpened}
          handleToggle={handleToggle}
        />

        {isTransactionsOpened && (
          <div className="my-3">
            <div className="text-muted-foreground grid grid-cols-[1fr_1fr_1fr_1fr_1fr] gap-6 border-b border-neutral-200 px-2 py-0 pb-4 text-xs font-medium">
              <div className="text-center">{t("billing.payment")}</div>
              <div className="text-center">{t("billing.request")}</div>
              <div className="text-center">{t("billing.price")}</div>
              <div className="text-center">{t("billing.time")}</div>
              <div className="text-center">{t("billing.createdBy")}</div>
            </div>

            <div className="divide-y divide-neutral-200 border-b border-neutral-200 px-2">
              {transactions.length ? (
                transactions.map((transaction) => (
                  <TransactionRow key={transaction.id} tx={transaction} />
                ))
              ) : (
                <p className="text-muted-foreground py-6 text-center text-sm">
                  {t("billing.noTransactionsFound")}
                </p>
              )}
            </div>
          </div>
        )}

        <ToggleButton
          title={t("billing.transactionLogs")}
          isOpened={isLogsOpened}
          handleToggle={handleToggleLogs}
        />

        {isLogsOpened && (
          <div className="my-3">
            <div className="text-muted-foreground grid grid-cols-[100px_auto] gap-6 border-b border-neutral-200 px-2 py-0 pb-4 text-xs font-medium">
              <div className="text-left font-medium">{t("billing.time")}</div>
              <div className="text-right">{t("billing.logs")}</div>
            </div>

            <div className="divide-y divide-neutral-200 border-b border-neutral-200 px-2">
              {transactions.length ? (
                transactions.map((transaction) => (
                  <TransactionLogRow key={transaction.id} tx={transaction} />
                ))
              ) : (
                <p className="text-muted-foreground py-6 text-center text-sm">
                  {t("billing.noTransactionsLogsFound")}
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

const WalletSummarySection = ({
  summary,
  bookingId,
  canManageWallet,
}: {
  summary: NonNullable<ReturnType<typeof useBookingPaymentSummary>["data"]>;
  bookingId?: string;
  canManageWallet: boolean;
}) => {
  const { t } = useTranslation();

  return (
    <div className="mb-4 flex w-full flex-col gap-4">
      <div className="grid gap-3 md:grid-cols-3">
        <WalletSummaryCard
          icon={<Banknote className="h-5 w-5 text-emerald-600" />}
          title={t("billing.walletOutstanding")}
          value={formatMoney(summary.outstandingAmount, summary.currency)}
          hint={t("billing.walletOutstandingHint")}
        />
        <WalletSummaryCard
          icon={<Wallet className="text-skyline-2 h-5 w-5" />}
          title={t("billing.walletBalance")}
          value={formatMoney(summary.walletBalanceAmount, summary.currency)}
          hint={t("billing.walletBalanceHint")}
        />
        <WalletSummaryCard
          icon={<CreditCard className="h-5 w-5 text-amber-600" />}
          title={t("billing.walletStripeRemainder")}
          value={formatMoney(summary.stripeRemainderAmount, summary.currency)}
          hint={t("billing.walletStripeRemainderHint")}
        />
      </div>

      <div className="rounded-2xl border border-neutral-200 bg-white p-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="space-y-1">
            <h3 className="text-foreground text-sm font-semibold">
              {t("billing.walletSplitTitle")}
            </h3>
            <p className="text-muted-foreground text-sm">
              {summary.outstandingAmount > 0
                ? t("billing.walletSplitHint", {
                    wallet: formatMoney(
                      summary.walletAppliedAmount,
                      summary.currency,
                    ),
                    stripe: formatMoney(
                      summary.stripeRemainderAmount,
                      summary.currency,
                    ),
                  })
                : t("billing.walletNoOutstanding")}
            </p>
          </div>

          {canManageWallet && bookingId ? (
            <WalletTopUpDialog
              bookingId={bookingId}
              currency={summary.currency}
            />
          ) : null}
        </div>

        <div className="bg-light-zinc mt-4 rounded-2xl border border-neutral-200">
          <div className="flex items-center justify-between border-b border-neutral-200 px-4 py-3">
            <span className="text-foreground text-sm font-semibold">
              {t("billing.walletActivity")}
            </span>
            <span className="text-muted-foreground text-xs">
              {t("billing.balance")}
            </span>
          </div>

          {summary.recentWalletEntries.length > 0 ? (
            <div className="divide-y divide-neutral-200">
              {summary.recentWalletEntries.map((entry) => (
                <WalletActivityRow key={entry.id} entry={entry} />
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground px-4 py-6 text-center text-sm">
              {t("billing.walletNoActivity")}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

const WalletTopUpDialog = ({
  bookingId,
  currency,
}: {
  bookingId: string;
  currency: string;
}) => {
  const { t } = useTranslation();
  const topUpGuestWallet = useTopUpGuestWallet(bookingId);
  const [open, setOpen] = useState(false);
  const [amountInput, setAmountInput] = useState("");
  const [note, setNote] = useState("");
  const [amountError, setAmountError] = useState("");

  const handleReset = () => {
    setAmountInput("");
    setNote("");
    setAmountError("");
  };

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);

    if (!nextOpen) {
      handleReset();
    }
  };

  const handleSubmit = async () => {
    const normalizedAmount = amountInput.trim().replace(",", ".");
    const parsedAmount = Number(normalizedAmount);

    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
      setAmountError(t("billing.walletTopUpAmountInvalid"));
      return;
    }

    const amountMinor = Math.round(parsedAmount * 100);

    if (amountMinor < 1) {
      setAmountError(t("billing.walletTopUpAmountInvalid"));
      return;
    }

    try {
      await topUpGuestWallet.mutateAsync({
        amount: amountMinor,
        note: note.trim() || undefined,
      });
      toast.success(
        t("billing.walletTopUpSuccess", {
          amount: formatMoney(amountMinor, currency),
        }),
      );
      handleOpenChange(false);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : t("billing.walletTopUpFailed");
      toast.error(message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button type="button" variant="new_default">
          <Banknote className="h-4 w-4" />
          {t("billing.walletTopUp")}
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white">
        <DialogHeader>
          <DialogTitle>{t("billing.walletTopUpTitle")}</DialogTitle>
          <DialogDescription>
            {t("billing.walletTopUpDescription")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Input
            type="number"
            min="0"
            step="0.01"
            inputMode="decimal"
            value={amountInput}
            onChange={(event) => {
              setAmountInput(event.target.value);
              if (amountError) {
                setAmountError("");
              }
            }}
            label={t("billing.walletTopUpAmount")}
            placeholder={t("billing.walletTopUpAmountPlaceholder", {
              currency: currency.toUpperCase(),
            })}
            aria-invalid={amountError ? "true" : "false"}
            description={t("billing.walletTopUpAmountDescription", {
              currency: currency.toUpperCase(),
            })}
          />

          {amountError ? (
            <p className="text-custom-destructive-dark text-sm">
              {amountError}
            </p>
          ) : null}

          <Textarea
            value={note}
            onChange={(event) => setNote(event.target.value)}
            label={t("billing.walletTopUpNote")}
            placeholder={t("billing.walletTopUpNotePlaceholder")}
            maxLength={500}
          />
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="new_outline"
            onClick={() => handleOpenChange(false)}
            disabled={topUpGuestWallet.isPending}
          >
            {t("common.cancel")}
          </Button>
          <Button
            type="button"
            variant="new_default"
            onClick={() => void handleSubmit()}
            disabled={topUpGuestWallet.isPending}
          >
            {topUpGuestWallet.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : null}
            {topUpGuestWallet.isPending
              ? t("billing.walletTopUpSubmitting")
              : t("billing.walletTopUpSubmit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const WalletSummaryCard = ({
  icon,
  title,
  value,
  hint,
}: {
  icon: React.ReactNode;
  title: string;
  value: string;
  hint: string;
}) => {
  return (
    <div className="bg-light-zinc rounded-2xl border border-neutral-200 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1">
          <p className="text-zinc text-sm font-medium">{title}</p>
          <p className="text-foreground text-xl font-semibold">{value}</p>
        </div>
        <div className="rounded-xl bg-white p-2 shadow-sm">{icon}</div>
      </div>
      <p className="text-muted-foreground mt-3 text-xs">{hint}</p>
    </div>
  );
};

const WalletActivityRow = ({ entry }: { entry: GuestWalletEntry }) => {
  const { t } = useTranslation();
  const isDebit = entry.type === GuestWalletEntryType.CONSUMPTION;
  let typeLabel = t("billing.walletEntryAdjustment");
  if (entry.type === GuestWalletEntryType.TOP_UP) {
    typeLabel = t("billing.walletEntryTopUp");
  } else if (entry.type === GuestWalletEntryType.CONSUMPTION) {
    typeLabel = t("billing.walletEntryConsumption");
  }
  const actorName = getStringMeta(entry.metadata ?? undefined, "actorName");
  const source = getStringMeta(entry.metadata ?? undefined, "source");
  const subtitle =
    entry.note || actorName || source || t("billing.walletEntryNoDetails");

  return (
    <div className="grid gap-3 px-4 py-3 md:grid-cols-[minmax(0,1fr)_140px_160px] md:items-center">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span
            className={cn(
              "flex h-8 w-8 items-center justify-center rounded-full",
              isDebit
                ? "bg-rose-50 text-rose-600"
                : "bg-emerald-50 text-emerald-600",
            )}
          >
            {isDebit ? (
              <ArrowDownLeft className="h-4 w-4" />
            ) : (
              <ArrowUpRight className="h-4 w-4" />
            )}
          </span>

          <div className="min-w-0">
            <p className="text-foreground truncate text-sm font-medium">
              {typeLabel}
            </p>
            <p className="text-muted-foreground truncate text-xs">{subtitle}</p>
          </div>
        </div>
      </div>

      <div className="text-muted-foreground text-left text-xs md:text-center">
        <div>{formatDateTime(entry.createdAt, "dd.MM.yyyy")}</div>
        <div>{formatDateTime(entry.createdAt, "HH:mm")}</div>
      </div>

      <div className="flex items-center justify-between gap-4 md:justify-end">
        <div
          className={cn(
            "text-sm font-semibold",
            isDebit ? "text-rose-600" : "text-emerald-600",
          )}
        >
          {isDebit ? "-" : "+"}
          {formatMoney(entry.amount, entry.currency)}
        </div>
        <div className="text-muted-foreground text-xs">
          {formatMoney(entry.balanceAfter, entry.currency)}
        </div>
      </div>
    </div>
  );
};

const getStatusLog = (
  tx: BillingTransaction,
  locales: {
    successStatuses: {
      success: string;
      successOffline: string;
      refund: string;
      partialRefund: string;
    };
    failureStatuses: {
      notPaid: string;
      notRefunded: string;
      notCompleted: string;
    };
  },
) => {
  const isSuccess = tx.status === BillingTransactionStatus.SUCCEEDED;

  if (isSuccess) {
    if (tx.type === BillingTransactionType.PAYMENT)
      return tx.paymentMethod === BillingPaymentMethod.OFFLINE
        ? locales.successStatuses.successOffline
        : locales.successStatuses.success;
    if (tx.type === BillingTransactionType.REFUND)
      return locales.successStatuses.refund;

    return locales.successStatuses.partialRefund;
  }

  if (tx.type === BillingTransactionType.PAYMENT)
    return locales.failureStatuses.notPaid;
  if (tx.type === BillingTransactionType.REFUND)
    return locales.failureStatuses.notRefunded;

  return locales.failureStatuses.notCompleted;
};

const TransactionLogRow = ({ tx }: { tx: BillingTransaction }) => {
  const { t } = useTranslation();

  const locales = {
    successStatuses: {
      success: `${t("common.madeVia")} ${t("billing.paymentSuccess")}`,
      successOffline: `${t("common.madeVia")} ${t("billing.paymentSuccessOffline")}`,
      refund: `${t("common.is")} ${t("billing.refunded")}`,
      partialRefund: `${t("common.is")} ${t("billing.partiallyRefunded")}`,
    },
    failureStatuses: {
      notPaid: `${t("common.is")} ${t("billing.failed")}`,
      notRefunded: `${t("common.is")} ${t("billing.paymentNotRefund")}`,
      notCompleted: `${t("common.is")} ${t("billing.paymentNotCompleted")}`,
    },
  };

  const description =
    tx.billingItems?.map((item) => item.description).join(", ") || "";
  const distance = getDistance(new Date(tx.createdAt), true);
  const statusLog = getStatusLog(tx, locales).toLowerCase();
  let transactionTypeLabel = t("billing.payment").toLowerCase();
  if (tx.type === BillingTransactionType.REFUND) {
    transactionTypeLabel = t("billing.refund").toLowerCase();
  } else if (tx.type === BillingTransactionType.PARTIAL_REFUND) {
    transactionTypeLabel = t("billing.partialRefund").toLowerCase();
  }
  const log = `${transactionTypeLabel} ${t("common.of")} ${formatMoney(tx.amount, tx.currency)} ${t("common.for")} ${description} ${statusLog}.`;

  return (
    <div className="text-foreground grid h-13 grid-cols-[100px_auto] items-center gap-6 py-1.5 text-xs">
      <span className="text-left font-medium">{distance}</span>
      <span className="text-right first-letter:uppercase">{log}</span>
    </div>
  );
};

const TransactionRow = ({ tx }: { tx: BillingTransaction }) => {
  const { t } = useTranslation();
  const paymentUi = getPaymentUi(tx, {
    successStatuses: {
      success: t("billing.paymentSuccess"),
      successOffline: t("billing.paymentSuccessOffline"),
      refund: t("billing.paymentRefund"),
      partialRefund: t("billing.partialRefund"),
    },
    failureStatuses: {
      notPaid: t("billing.paymentFailed"),
      notRefunded: t("billing.paymentNotRefund"),
      notCompleted: t("billing.paymentNotCompleted"),
    },
  });
  const sourceLabel = getTransactionSourceLabel(tx, t);
  const createdByName = getStringMeta(tx.metadata, "createdByName");
  const createdByRole = getStringMeta(tx.metadata, "createdByRole");

  const description =
    tx.billingItems?.map((item) => item.description).join(", ") || "";

  return (
    <div className="grid grid-cols-[1fr_1fr_1fr_1fr_1fr] items-center gap-6 py-1.5">
      <div className="flex flex-col items-center justify-center gap-1">
        <span
          className={cn(
            paymentUi.style,
            "flex w-fit items-center justify-center rounded-lg border px-2 py-1 text-xs",
          )}
        >
          {paymentUi.title}
        </span>
        {sourceLabel ? (
          <span className="text-muted-foreground text-center text-[10px]">
            {sourceLabel}
          </span>
        ) : null}
      </div>

      <span className="bg-light-indigo text-indigo flex h-10 items-center justify-center rounded-lg p-1 text-[10px] font-semibold">
        {description || ""}
      </span>

      <span className="text-foreground text-center text-[10px]">
        {formatMoney(tx.amount, tx.currency)}
      </span>

      <div className="flex flex-col items-center justify-center text-center text-[10px]">
        <span className="text-foreground">
          {formatDateTime(tx.createdAt, "dd.MM.yyyy")}
        </span>
        <span className="text-neutral-500">
          {formatDateTime(tx.createdAt, "HH:mm")}
        </span>
      </div>

      <div className="flex items-center justify-center gap-2">
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#93B4D6]">
          <User2 className="h-5 w-5 text-white" />
        </div>

        <div className="flex flex-col items-center justify-center text-[10px]">
          <span className="text-foreground font-medium">
            {createdByName || "—"}
          </span>
          <span className="text-muted-foreground">{createdByRole || ""}</span>
        </div>
      </div>
    </div>
  );
};

const getTransactionSourceLabel = (
  tx: BillingTransaction,
  t: (key: string) => string,
) => {
  const paymentSource = getStringMeta(tx.metadata, "paymentSource");

  if (paymentSource === "GUEST_WALLET") {
    return t("billing.walletSourceBalance");
  }

  if (paymentSource === "STRIPE_REMAINDER") {
    return t("billing.walletSourceStripeRemainderLabel");
  }

  if (
    tx.type === BillingTransactionType.PAYMENT &&
    tx.paymentMethod === BillingPaymentMethod.OFFLINE
  ) {
    return t("billing.walletSourceOfflineCash");
  }

  return undefined;
};
